import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import classNames from 'classnames';
import { CSSProperties, JSX } from 'react';
import CustomTooltip from '../customTooltip';
import type { ICustomButtonProps } from './types';
import { useCustomButton } from './useCustomButton';
import './styles.scss';

const EMPTY_STYLES: CSSProperties = {};
/**
 * Default no-op handler standing in for an absent optional callback prop of the custom button. Accepts no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

// Source
// https://www.figma.com/design/9Oxwy2CEJoMVdnlElc7haB/BTCY-Consumer---Core-components?node-id=6-1285&p=f&t=Hw1W6gVGyzyCkTYM-0

export const CustomButton = ({
  ariaLabel,
  tooltipTitle = '',
  size = 'middle',
  style = 'default',
  type = 'primary',
  htmlType = 'button',
  styles = EMPTY_STYLES,
  isRightIcon = false,
  children = undefined,
  icon = undefined,
  tooltipClassName = '',
  tooltipPlacement = 'top',
  loading = false,
  onMouseDown = NOOP,
  block = false,
  disabled = false,
  id = '',
  className,
  ...props
}: ICustomButtonProps): JSX.Element => {
  const { onClick } = useCustomButton(props as ICustomButtonProps);

  return (
    <CustomTooltip
      title={tooltipTitle}
      placement={tooltipPlacement}
      classNames={{ root: tooltipClassName }}
    >
      <button
        id={id}
        type={htmlType}
        aria-label={ariaLabel}
        className={classNames(
          'custom-button',
          `custom-button__${type}`,
          `custom-button__${size}`,
          `custom-button__style__${style}`,
          { 'custom-button__disabled': disabled || loading },
          { 'custom-button__loading': loading },
          { 'custom-button__right-icon': isRightIcon },
          { 'custom-button__icon-only': !children && icon },
          { 'custom-button__block': block },
          className,
        )}
        onClick={onClick}
        onMouseDown={onMouseDown}
        style={styles}
        disabled={disabled || loading}
      >
        {loading ? (
          <Spin
            style={{ color: 'inherit !important' }}
            indicator={
              <LoadingOutlined
                spin
                style={{
                  fontSize: size === 'small' ? '20px' : '24px',
                  color: 'inherit !important',
                }}
              />
            }
          />
        ) : (
          icon && (
            <span className="custom-button__icon" aria-hidden="true">
              {icon}
            </span>
          )
        )}
        {children && <span>{children}</span>}
      </button>
    </CustomTooltip>
  );
};

export default CustomButton;
